import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tick02Icon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";

const CHECKLIST_ITEMS = [
  "Saving your preferences",
  "Preparing your first project",
  "Applying your content style",
];

interface SetupTransitionStepProps {
  onComplete: () => void;
}

export function SetupTransitionStep({ onComplete }: SetupTransitionStepProps) {
  const [completedItems, setCompletedItems] = useState<number>(0);

  useEffect(() => {
    const timers = CHECKLIST_ITEMS.map((_, i) =>
      setTimeout(() => setCompletedItems(i + 1), (i + 1) * 800)
    );

    // Auto-advance after all items complete
    const finalTimer = setTimeout(onComplete, CHECKLIST_ITEMS.length * 800 + 1200);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(finalTimer);
    };
  }, [onComplete]);

  return (
    <div className="flex flex-col items-center text-center">
      <h1 className="font-display text-3xl font-normal text-foreground">
        Setting up your workspace
      </h1>

      <div className="mt-10 space-y-4">
        {CHECKLIST_ITEMS.map((item, i) => (
          <motion.div
            key={item}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.3, duration: 0.4 }}
            className="flex items-center gap-3"
          >
            <div
              className={cn(
                "flex size-6 items-center justify-center rounded-full transition-all duration-500",
                i < completedItems
                  ? "bg-foreground"
                  : "border-2 border-foreground/20",
              )}
            >
              {i < completedItems && (
                <HugeiconsIcon icon={Tick02Icon} className="size-3.5 text-background" />
              )}
            </div>
            <span
              className={cn(
                "text-sm transition-colors duration-300",
                i < completedItems ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {item}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Spinner while processing */}
      <div className="mt-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    </div>
  );
}
